import React, { useCallback, useContext, useState } from "react";
import { useIntl } from "react-intl";
import PhoneInput from "react-phone-number-input";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import wembiiLogoWhite from "../../assets/wembii-logo-white.png";
import wembiiLogo from "../../assets/wembii-logo.png";
import { ACCOUNT_POST_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../../cores/globals";
import { useRouterCore } from "../../cores/router";
import { useUserCore } from "../../cores/user";
import { Context } from "../../LocaleWrapper";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { LocalSelector } from "../component/LocaleSelector";
import useVisibility from "../../hooks/useVisibility/useVisibility.hook";
import { faEyeSlash, faEye, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuthPageTheme } from "../../hooks/useAuthPageTheme/useAuthPageTheme";

export const CreateAccount = () => {
  const intl = useIntl();
  const { locale } = useContext(Context);
  const { routes, goToLogin } = useRouterCore();
  const { postRegisterUser } = useUserCore();
  const { isLoading } = useGlobalCore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [firstName, setFirtName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");
  const [phoneType, setPhoneType] = useState("");
  const { toggleVisibility, visible } = useVisibility({ initValue: false });
  const { theme, toggleTheme } = useAuthPageTheme();

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    postRegisterUser(
      {
        email,
        password,
        firstName,
        lastName,
        phoneNumber,
        locale,
        phoneType,
        country,
      },
      goToLogin
    );
  };

  const isEmailValid = useCallback(() => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  }, [email]);

  const isPasswordValid = () => {
    const passwordRegex = /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]))(?=.{6,32}$)/;
    return passwordRegex.test(password);
  };

  const isRepeatPasswordValid = () => {
    return password === passwordConfirmation;
  };

  const disabled = useCallback(
    () =>
      !email ||
      !password ||
      !passwordConfirmation ||
      !firstName ||
      !lastName ||
      !phoneNumber ||
      !country ||
      !phoneType ||
      password !== passwordConfirmation ||
      !isEmailValid(),
    [
      email,
      password,
      passwordConfirmation,
      firstName,
      lastName,
      phoneNumber,
      country,
      phoneType,
      isEmailValid,
    ]
  );

  const isLight = theme === "light";

  return (
    <div
      id="register"
      className={`login-v2 login-v2--register${isLight ? " login-v2--light" : ""}`}
    >
      <div className="login-v2__hero" aria-hidden="true">
        <div className="login-v2__hero-bg" />
      </div>
      <div className="login-v2__panel">
        <div className="login-v2__panel-inner">
          <div className="login-v2__top-row">
            <div className="login-v2__brand">
              <img
                src={isLight ? wembiiLogo : wembiiLogoWhite}
                alt="Wembii"
                className="login-v2__logo"
              />
            </div>
            <button
              type="button"
              className="login-v2__theme-toggle"
              onClick={toggleTheme}
              aria-label={
                isLight
                  ? intl.formatMessage({ id: "app.authTheme.switchToDark" })
                  : intl.formatMessage({ id: "app.authTheme.switchToLight" })
              }
              title={
                isLight
                  ? intl.formatMessage({ id: "app.authTheme.switchToDark" })
                  : intl.formatMessage({ id: "app.authTheme.switchToLight" })
              }
            >
              <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
            </button>
          </div>

          <div className="login-v2__form-wrap">
            <h1 className="login-v2__title">
              {intl.formatMessage({ id: "app.createAnAccount" })}
            </h1>
            <p className="login-v2__subtitle">
              {intl.formatMessage({ id: "app.haveaccount" })}{" "}
              <Link to={routes.login} className="login-v2__link">
                {intl.formatMessage({ id: "app.login" })}
              </Link>
            </p>

            <Form onSubmit={onFormSubmit} className="login-v2__form">
              <FormGroup className="login-v2__field">
                <Label for="ca-name" className="login-v2__label">
                  {intl.formatMessage({ id: "app.nameTxt" })}
                </Label>
                <Input
                  type="text"
                  name="name"
                  id="ca-name"
                  placeholder={intl.formatMessage({ id: "app.nameTxt" })}
                  value={firstName}
                  onChange={(e) => setFirtName(e.target.value)}
                  className="login-v2__input"
                />
              </FormGroup>
              <FormGroup className="login-v2__field">
                <Label for="ca-lastName" className="login-v2__label">
                  {intl.formatMessage({ id: "app.surnameTxt" })}
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  id="ca-lastName"
                  placeholder={intl.formatMessage({ id: "app.surnameTxt" })}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="login-v2__input"
                />
              </FormGroup>
              <FormGroup className="login-v2__field">
                <Label for="ca-country" className="login-v2__label">
                  {intl.formatMessage({ id: "app.countryTxt" })}
                </Label>
                <Input
                  type="text"
                  name="country"
                  id="ca-country"
                  placeholder={intl.formatMessage({ id: "app.countryTxt" })}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="login-v2__input"
                />
              </FormGroup>
              <FormGroup className="login-v2__field">
                <Label for="ca-email" className="login-v2__label">
                  {intl.formatMessage({ id: "app.emailTxt" })}
                </Label>
                <Input
                  type="text"
                  name="email"
                  id="ca-email"
                  placeholder={intl.formatMessage({ id: "app.emailTxt" })}
                  invalid={!!email && !isEmailValid()}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-v2__input"
                />
                {!!email && !isEmailValid() && (
                  <FormFeedback>
                    {intl.formatMessage({ id: "app.emailIsNotValid" })}
                  </FormFeedback>
                )}
              </FormGroup>
              <FormGroup className="login-v2__field">
                <Label for="ca-phonetype" className="login-v2__label">
                  {intl.formatMessage({ id: "app.phoneTypeTxt" })}
                </Label>
                <Input
                  type="select"
                  name="phonetype"
                  id="ca-phonetype"
                  value={phoneType}
                  onChange={(e) => setPhoneType(e.target.value)}
                  className="login-v2__input login-v2__select"
                >
                  <option value=""></option>
                  <option value="celphone">Celular</option>
                  <option value="telephone">Teléfono fijo</option>
                </Input>
              </FormGroup>
              <FormGroup className="login-v2__field">
                <Label for="ca-phone" className="login-v2__label">
                  {intl.formatMessage({ id: "app.phoneTxt" })}
                </Label>
                <div className="login-v2__phone">
                  <PhoneInput
                    name="phone"
                    id="ca-phone"
                    placeholder={intl.formatMessage({ id: "app.phoneTxt" })}
                    value={phoneNumber}
                    onChange={(v) => setPhoneNumber(v || "")}
                  />
                </div>
              </FormGroup>
              <FormGroup className="login-v2__field">
                <Label for="ca-password" className="login-v2__label">
                  {intl.formatMessage({ id: "app.passwordTxt" })}
                </Label>
                <div className="login-v2__password-wrap login-v2__password-wrap--bordered">
                  <Input
                    type={visible ? "text" : "password"}
                    invalid={!!password && !isPasswordValid()}
                    name="password"
                    id="ca-password"
                    placeholder={intl.formatMessage({
                      id: "app.passwordTxt",
                    })}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-v2__input login-v2__input--password"
                  />
                  <button
                    type="button"
                    onClick={toggleVisibility}
                    className="login-v2__toggle-password"
                    aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                  </button>
                </div>
                {!!password && !isPasswordValid() && (
                  <p className="login-v2__text-danger">
                    {intl.formatMessage({ id: "app.passwordIsNotValid" })}
                  </p>
                )}
              </FormGroup>

              <FormGroup className="login-v2__field">
                <Label for="ca-password-confirmation" className="login-v2__label">
                  {intl.formatMessage({ id: "app.confirmPaswoord" })}
                </Label>
                <Input
                  type="password"
                  name="passwordConfirmation"
                  id="ca-password-confirmation"
                  placeholder={intl.formatMessage({
                    id: "app.confirmPaswoord",
                  })}
                  invalid={
                    !!password &&
                    !!passwordConfirmation &&
                    !isRepeatPasswordValid()
                  }
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="login-v2__input"
                />
                {!!password &&
                  !!passwordConfirmation &&
                  !isRepeatPasswordValid() && (
                    <FormFeedback>
                      {intl.formatMessage({ id: "app.passwordsDoNotMatch" })}
                    </FormFeedback>
                  )}
              </FormGroup>
              <UpdateLoadingCard
                loadingId={ACCOUNT_POST_LOADING}
                text={intl.formatMessage({ id: "app.creatingAccount" })}
              />
              {!isLoading(ACCOUNT_POST_LOADING) && (
                <Button
                  type="submit"
                  className="login-v2__submit"
                  disabled={disabled()}
                >
                  {intl.formatMessage({ id: "app.createAccount" })}
                </Button>
              )}

              <div
                className={`login-v2__locale ${
                  isLight ? "login-v2__locale--light" : "login-v2__locale--dark"
                }`}
              >
                <LocalSelector />
              </div>
            </Form>
          </div>

          <div className="login-v2__footer">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.wembii.com/terminos-y-condiciones/"
              className="login-v2__legal"
            >
              {intl.formatMessage({ id: "app.terms" })}
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.wembii.com/privacy-policy/"
              className="login-v2__legal"
            >
              {intl.formatMessage({ id: "app.privacyPolicy" })}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
